import React from 'react';
import { useLocalization } from '../../../hooks/useLocalization';
import DestinationsContainer from './destinations/DestinationsContainer';

// Debug version marker
const CONTENT_VERSION = 'v11-fix-2025-01-13';

interface DestinationsContentProps {
    currentUser?: any;
    theme?: string;
    language?: 'en' | 'sv';
}

// Error Boundary Component
class ContentErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean; error: Error | null; errorInfo: React.ErrorInfo | null }
> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('ContentErrorBoundary caught error:', error);
        console.error('Component Stack:', errorInfo.componentStack);
        this.setState({ errorInfo });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="p-8 bg-red-50 dark:bg-red-900/20 border-2 border-red-400 rounded-xl m-4">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">
                        ⚠️ Destinations Error (Content v10)
                    </h2>
                    <p className="text-red-700 mb-4">
                        <strong>Error:</strong> {this.state.error?.message}
                    </p>
                    <details className="mt-4" open>
                        <summary className="cursor-pointer text-red-600 font-medium">
                            Stack Trace
                        </summary>
                        <pre className="mt-2 p-4 bg-red-100 dark:bg-red-900/40 rounded text-xs overflow-auto max-h-60 whitespace-pre-wrap">
                            {this.state.errorInfo?.componentStack}
                        </pre>
                    </details>
                    <button
                        onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                        Try Again
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

const DestinationsContent: React.FC<DestinationsContentProps> = ({ currentUser, theme }) => {
    // Log immediately to confirm this component loads
    console.log(`[ANTIGRAVITY] DestinationsContent ${CONTENT_VERSION} LOADING`);

    const { language } = useLocalization();

    // Ensure theme is valid
    const validTheme = theme === 'dark' ? 'dark' : 'light';

    // Map language - Lumi supports 'en' and 'sv'
    const validLanguage = language === 'sv' ? 'sv' : 'en';

    // Mock user if not provided
    const user = currentUser || {
        id: 'dev_user',
        name: 'Developer',
        role: 'admin',
        email: 'dev@example.com'
    };

    return (
        <ContentErrorBoundary>
            <div className="w-full h-full">
                <DestinationsContainer
                    currentUser={user}
                    theme={validTheme}
                    language={validLanguage as any}
                />
            </div>
        </ContentErrorBoundary>
    );
};

export default DestinationsContent;
